import { useEffect, useState } from 'react';
import { useApi } from '../../lib/ApiProvider';
import type { ExifPolicyDto } from '../../lib/api';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';

/**
 * LOT 10E — Configuration › Confidentialité des preuves (EXIF/RGPD). Réglages simples et versionnés :
 * strip des métadonnées à l'import (défaut), autorisation de télécharger l'original, durée de
 * conservation des preuves non utilisées (informative, jamais exécutée automatiquement au MVP).
 *
 * AVERTISSEMENT : la politique de conservation/effacement doit être validée par un juriste burkinabè
 * ou la CIL avant mise en production — cet écran ne garantit pas une conformité juridique définitive.
 */
export function ExifPolicyPage(): JSX.Element {
  const api = useApi();
  const [stripAll, setStripAll] = useState(true);
  const [allowOriginal, setAllowOriginal] = useState(false);
  const [retention, setRetention] = useState<string>('');
  const [history, setHistory] = useState<ExifPolicyDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const reload = async (): Promise<void> => {
    try {
      const active = await api.getExifPolicy();
      if (active) {
        setStripAll(active.stripAllMetadataOnImport);
        setAllowOriginal(active.allowOriginalDownload);
        setRetention(active.unusedRetentionDays == null ? '' : String(active.unusedRetentionDays));
      }
      setHistory(await api.exifPolicyHistory());
    } catch (e) {
      setError(toDisplayError(e).message);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const days = retention.trim() === '' ? null : Math.max(1, Number(retention));
      await api.saveExifPolicy({
        stripAllMetadataOnImport: stripAll,
        allowOriginalDownload: allowOriginal,
        unusedRetentionDays: days,
      });
      await reload();
    } catch (e) {
      setError(toDisplayError(e).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-4">
      <h1 className="text-xl font-semibold text-slate-800">
        Confidentialité des preuves (EXIF/RGPD)
      </h1>
      <Alert variant="warning">
        La politique de conservation et d’effacement doit être validée par un juriste burkinabè ou
        la CIL avant toute mise en production. Cet écran ne garantit pas une conformité juridique
        définitive.
      </Alert>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <Card>
        <CardHeader>
          <CardTitle>Réglages</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={stripAll}
              aria-label="Retirer les métadonnées à l’import"
              onChange={(e) => setStripAll(e.target.checked)}
            />
            Retirer les métadonnées (EXIF/GPS) à l’import — recommandé
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={allowOriginal}
              aria-label="Autoriser le téléchargement de l’original"
              onChange={(e) => setAllowOriginal(e.target.checked)}
            />
            Autoriser le téléchargement de l’original (rôle habilité)
          </label>
          <div className="flex items-center gap-2">
            <Label htmlFor="retention">Conservation des preuves non utilisées (jours)</Label>
            <Input
              id="retention"
              aria-label="Conservation des preuves non utilisées (jours)"
              type="number"
              min={1}
              className="w-24"
              value={retention}
              placeholder="—"
              onChange={(e) => setRetention(e.target.value)}
            />
            <span className="text-xs text-slate-400">jamais exécuté automatiquement (MVP)</span>
          </div>
          <div className="pt-2">
            <Button onClick={() => void save()} disabled={saving}>
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Historique</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-slate-500">Aucune version enregistrée.</p>
          ) : (
            <ul className="space-y-1 text-sm text-slate-700">
              {history.map((v) => (
                <li key={v.id}>
                  v{v.version} — {v.status} — strip:{String(v.stripAllMetadataOnImport)} — orig:
                  {String(v.allowOriginalDownload)} —{' '}
                  {new Date(v.createdAt).toLocaleString('fr-FR')}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
